import React, { useState, useEffect, useRef } from 'react';

const SuccessStories = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const testimonials = [
    {
      id: 1,
      image: "/images/img_couple_image.png",
      quote: "We were tired of traditional marriage agencies pushing profiles that did not align.",
      names: "Meera & Goutham",
      location: "Married Sep 2025 • Coimbatore & Singapore",
      rating: 5
    },
    {
      id: 2,
      image: "/images/img_couple_image_382x250.png",
      quote: "Unlike other platforms, every interaction felt genuine. The verification process gave both our families complete confidence.",
      names: "Monisha & Rahul",
      location: "Married Dec 2025 • Tiruppur & Chennai",
      rating: 5
    },
    {
      id: 3,
      image: "/images/img_profile_img.png",
      quote: "The Gothram checking engine and direct family connect saved us months of searching. We found our ideal life partner easily!",
      names: "Kavitha & Karthik",
      location: "Married Jan 2026 • Erode & Bengaluru",
      rating: 5
    },
    {
      id: 4,
      image: "/images/img_couple_smiling.png",
      quote: "Our families connected effortlessly through the platform's elder-supervised dashboard. We are extremely grateful!",
      names: "Priya & Suresh",
      location: "Married Feb 2026 • Salem & Chennai",
      rating: 5
    }
  ];

  // Detect mobile viewport (< 768px)
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Group testimonials: 1 per slide on mobile, 2 per slide on desktop
  const itemsPerSlide = isMobile ? 1 : 2;
  const slides = [];
  for (let i = 0; i < testimonials.length; i += itemsPerSlide) {
    slides.push(testimonials.slice(i, i + itemsPerSlide));
  }

  // Reset activeSlide if it exceeds slides bounds on viewport resize
  useEffect(() => {
    if (activeSlide >= slides.length) {
      setActiveSlide(0);
    }
  }, [slides.length, activeSlide]);

  const handlePrevious = () => {
    setActiveSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setActiveSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  // Auto-play timer (6 seconds), pauses when user hovers
  useEffect(() => {
    if (isHovered) return;
    const timer = setInterval(() => {
      handleNext();
    }, 6000);
    return () => clearInterval(timer);
  }, [activeSlide, isHovered, slides.length]);

  // Touch Swipe Handlers for Mobile
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const distance = touchStartX.current - touchEndX.current;
    if (distance > 40) {
      handleNext(); // Swiped left
    } else if (distance < -40) {
      handlePrevious(); // Swiped right
    }
    touchStartX.current = 0;
    touchEndX.current = 0;
  };

  return (
    <section id="stories" className="w-full py-12 sm:py-16 md:py-20 lg:py-24 border-text-primary/20 scroll-mt-16">
      <div className="w-full max-w-[1360px] mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center mb-8 sm:mb-12 md:mb-16">
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-wide bg-gradient-to-r from-[#8a216b] to-[#f03f16] bg-clip-text text-transparent mb-2 sm:mb-3">
            real journey
          </p>
          <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-[48px] font-semibold text-text-primary capitalize mb-3 sm:mb-5">
            Love Stories That Began Here
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-text-primary/60 max-w-2xl mx-auto px-2">
            A collection of real couples who found their perfect life partner through Appuppa Gounder Matrimony.
          </p>
        </div>

        {/* Testimonials Swiper */}
        <div
          className="relative mb-6 sm:mb-10"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="overflow-hidden rounded-2xl sm:rounded-[36px]">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${activeSlide * 100}%)` }}
            >
              {slides.map((slidePair, slideIdx) => (
                <div key={slideIdx} className="w-full flex-shrink-0 px-1">
                  <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-4 sm:gap-6 md:gap-8`}>
                    {slidePair.map((testimonial) => (
                      <div
                        key={testimonial.id}
                        className="bg-gradient-to-b from-[#fff2f4] to-white border border-primary-brand/40 rounded-2xl sm:rounded-[36px] p-5 sm:p-7 md:p-9 shadow-sm hover:shadow-md transition-all flex flex-col justify-between"
                      >
                        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 items-center sm:items-start">
                          {/* Image */}
                          <div className="w-full sm:w-2/5 flex-shrink-0">
                            <img
                              src={testimonial.image}
                              alt={testimonial.names}
                              className="w-full h-[200px] sm:h-[210px] md:h-[230px] object-cover object-top rounded-xl sm:rounded-2xl shadow-sm"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = '/images/img_couple_image.png';
                              }}
                            />
                          </div>

                          {/* Content */}
                          <div className="flex-1 flex flex-col justify-between w-full">
                            <div>
                              {/* Decorative Quotation Mark */}
                              <div className="text-4xl sm:text-5xl font-serif text-[#8a216b] font-extrabold leading-none mb-2 select-none">
                                &ldquo;
                              </div>

                              <p className="text-sm sm:text-base md:text-lg text-gray-700 mb-4 sm:mb-6 leading-relaxed font-medium">
                                {testimonial.quote}
                              </p>
                              <div className="mb-3 sm:mb-4">
                                <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-0.5">
                                  {testimonial.names}
                                </h3>
                                <p className="text-xs sm:text-sm text-gray-500 font-medium">
                                  {testimonial.location}
                                </p>
                              </div>
                              <div className="flex gap-1">
                                {[...Array(testimonial.rating)].map((_, i) => (
                                  <img
                                    key={i}
                                    src="/images/img_pixel_heart_solid.png"
                                    alt="Heart"
                                    className="w-4 h-4 sm:w-5 sm:h-5"
                                  />
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Controls & Swiper Dots */}
          <div className="flex items-center justify-between mt-6 sm:mt-8 max-w-xs mx-auto px-4">
            <button
              onClick={handlePrevious}
              className="p-2.5 sm:p-3 border border-[#f03f16] text-[#f03f16] hover:bg-gradient-to-r hover:from-[#8a216b] hover:to-[#f03f16] hover:text-white rounded-full shadow-md hover:shadow-lg transition-all cursor-pointer"
              aria-label="Previous testimonial"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 stroke-current fill-none" viewBox="0 0 24 24">
                <path d="M15 19l-7-7 7-7" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {/* Pagination Dots */}
            <div className="flex items-center gap-1.5 sm:gap-2">
              {slides.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveSlide(idx)}
                  className={`h-2 sm:h-2.5 rounded-full transition-all cursor-pointer ${activeSlide === idx
                    ? 'w-6 sm:w-8 bg-gradient-to-r from-[#8a216b] to-[#f03f16]'
                    : 'w-2 sm:w-2.5 bg-gray-300 hover:bg-gray-400'
                    }`}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>

            <button
              onClick={handleNext}
              className="p-2.5 sm:p-3 bg-gradient-to-r from-[#8a216b] to-[#f03f16] text-white rounded-full shadow-md hover:shadow-lg transition-all cursor-pointer"
              aria-label="Next testimonial"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 stroke-current fill-none" viewBox="0 0 24 24">
                <path d="M9 5l7 7-7 7" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SuccessStories;